import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Clock,
  ArrowRight,
  Check,
  ChevronDown,
  Shield,
  Lock,
  Download,
  Infinity as InfinityIcon,
  Zap,
  Star,
  Flame,
  Award,
  BookOpen,
  Headphones,
  Brain,
} from 'lucide-react';
import {
  modules,
  meditationTestimonials,
  meditationFaqs,
  BUNDLE_NAME,
  BUNDLE_PRICE,
  BUNDLE_ORIGINAL_PRICE,
  BUNDLE_SAVINGS,
  BUNDLE_DISCOUNT_PCT,
  PRIMARY_BUY_LINE,
  fmt,
} from '../mockMeditation';
import MeditationPurchaseNotifier from './MeditationPurchaseNotifier';
import SampleTrackPreview from './SampleTrackPreview';
import BuyCelebration from './BuyCelebration';

// Global CTA click handler set by the landing page to trigger celebration + navigate
let triggerClaim = () => {};

const accentMap = {
  emerald: {
    ring: 'border-emerald-400/40',
    chip: 'bg-emerald-500/10 border-emerald-400/30 text-emerald-200',
    icon: 'text-emerald-300',
    price: 'text-emerald-300',
    Icon: Headphones,
  },
  amber: {
    ring: 'border-amber-400/40',
    chip: 'bg-amber-500/10 border-amber-400/30 text-amber-200',
    icon: 'text-amber-300',
    price: 'text-amber-200',
    Icon: Brain,
  },
  sky: {
    ring: 'border-sky-400/40',
    chip: 'bg-sky-500/10 border-sky-400/30 text-sky-200',
    icon: 'text-sky-300',
    price: 'text-sky-200',
    Icon: BookOpen,
  },
};

function Countdown() {
  // 60-minute flash sale
  const [t, setT] = useState({ h: 0, m: 59, s: 59 });
  useEffect(() => {
    const id = setInterval(() => {
      setT((p) => {
        let { h, m, s } = p;
        s -= 1;
        if (s < 0) { s = 59; m -= 1; }
        if (m < 0) { m = 59; h -= 1; }
        if (h < 0) { h = 0; m = 59; s = 59; }
        return { h, m, s };
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);
  const pad = (n) => String(n).padStart(2, '0');
  return (
    <div className="inline-flex flex-wrap items-center justify-center gap-2 md:gap-3 px-4 md:px-6 py-2.5 md:py-3 rounded-full border border-amber-400/40 bg-black/40 backdrop-blur-md">
      <Clock size={16} className="text-amber-300" />
      <span className="text-amber-200 text-[11px] md:text-sm font-semibold uppercase tracking-widest">Flash Sale Ends In:</span>
      <span className="font-mono text-white text-base md:text-xl font-bold tabular-nums">
        {pad(t.h)}:{pad(t.m)}:{pad(t.s)}
      </span>
    </div>
  );
}

function FaqItem({ q, a, open, onClick }) {
  return (
    <div className={`rounded-xl border transition-colors ${open ? 'border-emerald-400/40 bg-white/[0.03]' : 'border-white/10 bg-white/[0.02] hover:border-white/20'}`}>
      <button onClick={onClick} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
        <span className="text-white font-semibold">{q}</span>
        <ChevronDown className={`text-emerald-300 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} size={20} />
      </button>
      <div className={`grid transition-all duration-300 ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
        <div className="overflow-hidden">
          <p className="px-5 pb-5 text-slate-300 text-sm leading-relaxed">{a}</p>
        </div>
      </div>
    </div>
  );
}

function ModuleValue({ value, priceClass }) {
  const ref = useRef(null);
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setTriggered(true);
      },
      { threshold: 0.55 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className="text-right">
      <div className="text-slate-500 text-[10px] md:text-xs uppercase tracking-widest">Module Value</div>
      <div className="relative inline-block mt-1">
        <span className={`font-extrabold text-2xl md:text-3xl transition-colors duration-500 ${triggered ? 'text-slate-500' : priceClass}`}>
          ${value}
          <span className="ml-1 text-[10px] md:text-xs font-bold tracking-widest opacity-70 align-middle">USD</span>
        </span>
        {triggered && <span className="mv-slash absolute inset-0 pointer-events-none" />}
      </div>
      <div className={`mt-1 overflow-hidden ${triggered ? 'mv-badge-in' : 'opacity-0'}`}>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] md:text-[10px] font-extrabold uppercase tracking-widest bg-gradient-to-r from-emerald-400 to-amber-300 text-slate-900 shadow-[0_4px_18px_-4px_rgba(16,185,129,0.6)]">
          <Sparkles size={10} /> Included Free Today
        </span>
      </div>
    </div>
  );
}

function ModuleCard({ mod, accent }) {
  const a = accent;
  const ModIcon = a.Icon;
  return (
    <div className={`rounded-3xl border ${a.ring} bg-gradient-to-b from-white/[0.04] to-transparent p-5 md:p-8`}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="flex items-start gap-3 md:gap-4">
          <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl border ${a.chip} flex items-center justify-center flex-shrink-0`}>
            <ModIcon className={a.icon} size={22} />
          </div>
          <div className="min-w-0">
            <div className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${a.chip}`}>
              {mod.tag}
            </div>
            <h3 className="mt-2 text-white font-extrabold text-lg md:text-2xl leading-tight">{mod.title}</h3>
            <p className="mt-1 text-slate-400 text-xs md:text-sm">{mod.subtitle}</p>
          </div>
        </div>
        <ModuleValue value={mod.value} priceClass={a.price} />
      </div>

      <div className="grid md:grid-cols-2 gap-2.5">
        {mod.items.map((it) => (
          <div
            key={it.title}
            className="group flex items-center gap-3 p-3 rounded-xl border border-white/8 bg-black/25 hover:border-emerald-400/30 transition-colors"
          >
            <div className="w-6 h-6 rounded-full bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center flex-shrink-0">
              <Check size={12} className="text-emerald-300" />
            </div>
            <h4 className="text-white font-semibold text-sm md:text-[15px] leading-snug">{it.title}</h4>
          </div>
        ))}
      </div>
    </div>
  );
}

function PrimaryCTA({ label = `Claim ${BUNDLE_NAME} for $${BUNDLE_PRICE} USD Now`, testId = 'landing-primary-cta' }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => triggerClaim()}
      className="group relative inline-flex items-center justify-center gap-3 px-8 py-5 rounded-full font-bold text-slate-900 text-base md:text-lg bg-gradient-to-r from-emerald-300 to-emerald-500 shadow-[0_0_50px_-5px_rgba(16,185,129,0.65)] hover:shadow-[0_0_80px_-5px_rgba(16,185,129,0.95)] transition-all"
    >
      <span className="absolute inset-0 rounded-full bg-emerald-300/25 blur-2xl -z-10 animate-pulse" />
      <span className="relative flex items-center gap-3">
        {label}
        <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
      </span>
    </button>
  );
}

function BuyLine() {
  return (
    <p className="mt-4 text-center text-slate-300 text-sm md:text-base italic max-w-2xl mx-auto">
      {PRIMARY_BUY_LINE}
    </p>
  );
}

export default function MeditationLanding() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [boom, setBoom] = useState(false);
  const [celebrate, setCelebrate] = useState(false);

  // Bind module-scope triggerClaim to the current component's setter
  triggerClaim = () => setCelebrate(true);

  const scrollToOffer = () => {
    document.querySelector('#offer')?.scrollIntoView({ behavior: 'smooth' });
  };

  // IntersectionObserver to trigger the BOOM animation when the offer section is in view
  useEffect(() => {
    const el = document.querySelector('#offer');
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !revealed) {
          setRevealed(true);
          setTimeout(() => setBoom(true), 500);
        }
      },
      { threshold: 0.35 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [revealed]);

  return (
    <div className="min-h-screen bg-[#050813] text-white antialiased overflow-x-hidden">
      {/* Local animations */}
      <style>{`
        @keyframes med-boom { 0% { transform: scale(0) rotate(-30deg); opacity: 0; } 60% { transform: scale(1.2) rotate(6deg); opacity: 1; } 100% { transform: scale(1) rotate(-8deg); opacity: 1; } }
        @keyframes med-slash { 0% { width: 0; opacity: 0; } 100% { width: 100%; opacity: 1; } }
        @keyframes med-price-in { 0% { transform: translateY(20px) scale(0.9); opacity: 0; } 100% { transform: translateY(0) scale(1); opacity: 1; } }
        @keyframes med-shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        .med-boom { animation: med-boom .8s cubic-bezier(.16,1.36,.5,1) forwards; }
        .med-slash::after { content: ''; position: absolute; left: 0; top: 50%; height: 4px; background: linear-gradient(90deg, transparent, #ef4444 20%, #ef4444 80%, transparent); border-radius: 2px; transform: translateY(-50%) rotate(-8deg); box-shadow: 0 0 12px rgba(239,68,68,0.9); animation: med-slash .6s .1s ease-out forwards; }
        .med-price-in { animation: med-price-in .7s .35s cubic-bezier(.16,1.36,.5,1) both; }
        .med-shimmer { background: linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.28) 50%, transparent 65%); background-size: 200% 100%; animation: med-shimmer 2.6s linear infinite; }

        /* Module value slash */
        @keyframes mv-slash-draw { 0% { width: 0; opacity: 0; } 100% { width: 108%; opacity: 1; } }
        @keyframes mv-badge-in { 0% { max-height: 0; opacity: 0; transform: translateY(-6px); } 100% { max-height: 40px; opacity: 1; transform: translateY(0); } }
        .mv-slash::after { content: ''; position: absolute; left: -4%; top: 50%; height: 3.5px; background: linear-gradient(90deg, transparent, #ef4444 15%, #ef4444 85%, transparent); border-radius: 2px; transform: translateY(-50%) rotate(-9deg); box-shadow: 0 0 14px rgba(239,68,68,0.95); animation: mv-slash-draw .7s .1s cubic-bezier(.2,.9,.3,1) forwards; }
        .mv-badge-in { animation: mv-badge-in .5s .55s cubic-bezier(.16,1.36,.5,1) both; }
      `}</style>

      {/* ============ HERO ============ */}
      <section className="relative pt-16 pb-24 overflow-hidden">
        <div
          className="absolute inset-0 opacity-40 pointer-events-none"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1677357623576-7c8aab08da22?w=1920&q=80')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050813]/70 via-[#050813]/85 to-[#050813]" />
        <div className="absolute left-1/2 top-1/3 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-amber-400/10 blur-3xl pointer-events-none" />

        <div className="relative max-w-5xl mx-auto px-6 text-center pt-10">
          <Countdown />

          {/* Urgency banner */}
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-rose-400/30 bg-rose-500/10 text-rose-200 text-xs md:text-sm font-semibold">
            <Flame size={14} />
            Special Launch Price: Reserved for the first 50 action-takers only.
          </div>

          <h1 className="mt-8 font-extrabold tracking-tight leading-[1.05] text-4xl md:text-6xl lg:text-7xl">
            <span className="text-white">Unlock Complete Calm, Deep Focus &amp; </span>
            <br className="hidden md:block" />
            <span className="bg-gradient-to-r from-emerald-300 via-amber-200 to-emerald-300 bg-clip-text text-transparent">
              Full-Spectrum Mind-Body Reset
            </span>
            <span className="text-white"> — in 10 Minutes a Day.</span>
          </h1>

          <p className="mt-6 max-w-3xl mx-auto text-slate-300 text-lg leading-relaxed">
            Instant lifetime access to <span className="text-white font-semibold">{BUNDLE_NAME}</span> — three complete modules of guided audio, frequency sessions, and workbooks engineered to eliminate stress, awaken dormant energy, and shift you into your highest frequency.
          </p>

          {/* Hero price row — original strikethrough vs. today's price */}
          <div
            data-testid="hero-price-row"
            className="mt-8 inline-flex flex-wrap items-baseline justify-center gap-3 md:gap-5 px-6 py-4 rounded-2xl border border-emerald-400/30 bg-black/40 backdrop-blur-md"
          >
            <div className="flex flex-col items-start">
              <span className="text-slate-500 text-[10px] md:text-xs uppercase tracking-widest">Original Price</span>
              <span className="text-slate-400 text-2xl md:text-3xl font-bold line-through decoration-rose-500 decoration-[3px]">
                {fmt(BUNDLE_ORIGINAL_PRICE)}
              </span>
            </div>
            <span className="text-slate-600 text-2xl md:text-3xl font-light">|</span>
            <div className="flex flex-col items-start">
              <span className="text-amber-200 text-[10px] md:text-xs uppercase tracking-widest font-bold">Today's Price</span>
              <span
                data-testid="hero-todays-price"
                className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-emerald-300 to-amber-200 bg-clip-text text-transparent leading-none"
              >
                {fmt(BUNDLE_PRICE)}
              </span>
            </div>
            <span className="ml-1 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-200 text-[11px] font-extrabold uppercase tracking-widest">
              Save {BUNDLE_DISCOUNT_PCT}%
            </span>
          </div>

          <div className="mt-8 flex flex-col items-center gap-4">
            <PrimaryCTA testId="hero-primary-cta" />
            <BuyLine />
            <div className="inline-flex items-center gap-2 text-slate-400 text-sm mt-1">
              <Zap size={14} className="text-amber-300 fill-amber-300" />
              Instant lifetime download links delivered to your email
            </div>

            {/* Trust strip — social proof, right under the CTA to lift conversion */}
            <div
              data-testid="hero-trust-strip"
              className="mt-3 flex flex-wrap items-center justify-center gap-4 md:gap-6 px-5 py-3 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm"
            >
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  {[
                    'https://images.pexels.com/photos/9159272/pexels-photo-9159272.jpeg?w=80&h=80&fit=crop&crop=faces',
                    'https://images.unsplash.com/photo-1573999842765-26f179ec3979?w=80&h=80&fit=crop&crop=faces',
                    'https://images.unsplash.com/photo-1617009762269-c062aaf6b3a0?w=80&h=80&fit=crop&crop=faces',
                  ].map((src) => (
                    <img key={src} src={src} alt="" decoding="async" width="28" height="28" className="w-7 h-7 rounded-full border-2 border-[#050813] object-cover" />
                  ))}
                </div>
                <div className="text-left leading-tight">
                  <div className="text-white text-xs md:text-sm font-bold">10,000+ souls</div>
                  <div className="text-slate-400 text-[10px] md:text-xs">already transformed</div>
                </div>
              </div>
              <div className="hidden md:block h-8 w-px bg-white/10" />
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={14} className="fill-amber-300 text-amber-300" />
                  ))}
                </div>
                <div className="text-left leading-tight">
                  <div className="text-white text-xs md:text-sm font-bold">4.9 / 5</div>
                  <div className="text-slate-400 text-[10px] md:text-xs">verified reviews</div>
                </div>
              </div>
              <div className="hidden md:block h-8 w-px bg-white/10" />
              <div className="flex items-center gap-2">
                <Lock size={18} className="text-emerald-300 flex-shrink-0" />
                <div className="text-left leading-tight">
                  <div className="text-white text-xs md:text-sm font-bold">Secure Checkout</div>
                  <div className="text-slate-400 text-[10px] md:text-xs">Encrypted &amp; private</div>
                </div>
              </div>
            </div>

            {/* Scarcity — spots remaining bar (drives urgency) */}
            <div data-testid="hero-scarcity" className="w-full max-w-md mt-1">
              <div className="flex items-center justify-between text-[11px] md:text-xs font-semibold mb-1.5">
                <span className="text-rose-300 inline-flex items-center gap-1.5">
                  <Flame size={12} /> Only 23 of 50 launch spots remaining
                </span>
                <span className="text-slate-400">54% claimed</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
                <div className="h-full w-[54%] bg-gradient-to-r from-rose-400 via-orange-400 to-amber-300" />
              </div>
            </div>

            {/* Value comparison — smart price anchoring */}
            <div className="mt-2 text-slate-400 text-xs md:text-sm text-center max-w-lg">
              <span className="text-slate-500">Compare:</span>{' '}
              <span className="line-through decoration-slate-500">$150+ USD / therapy session</span>{' '}
              <span className="text-slate-500">·</span>{' '}
              <span className="line-through decoration-slate-500">$40 USD / month meditation app</span>{' '}
              <span className="text-emerald-300 font-bold">→ ${BUNDLE_PRICE} USD lifetime, once.</span>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {[
              { icon: InfinityIcon, label: 'Lifetime Access' },
              { icon: Download, label: 'Instant Download' },
              { icon: Zap, label: 'Delivered in Minutes' },
              { icon: Lock, label: 'Secure Checkout' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center justify-center gap-2 text-slate-300 text-sm">
                <Icon size={16} className="text-emerald-300" />
                {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SAMPLE TRACK PREVIEW ============ */}
      <SampleTrackPreview />

      {/* ============ VALUE STACK ============ */}
      <section className="relative py-20 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-400/30 bg-amber-400/5 text-amber-200 text-xs font-semibold uppercase tracking-widest">
              What&apos;s Inside &middot; Complete Value Stack
            </div>
            <h2 className="mt-4 text-3xl md:text-5xl font-extrabold text-white tracking-tight">
              Three Modules. Every Track. Every Workbook.
              <br />
              <span className="bg-gradient-to-r from-emerald-300 to-amber-200 bg-clip-text text-transparent">Retail Value {fmt(BUNDLE_ORIGINAL_PRICE)}. Yours Today for {fmt(BUNDLE_PRICE)}.</span>
            </h2>
          </div>

          <div className="space-y-8 md:space-y-10">
            {modules.map((mod) => (
              <ModuleCard key={mod.id} mod={mod} accent={accentMap[mod.accent]} />
            ))}
          </div>
        </div>
      </section>

      {/* ============ ANIMATED PRICE REVEAL ============ */}
      <section id="offer" className="relative py-24 border-t border-white/5">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-emerald-500/10 blur-3xl" />
        </div>

        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-400/30 bg-emerald-500/5 text-emerald-200 text-xs font-semibold uppercase tracking-widest">
            The Unbeatable Offer
          </div>
          <h2 className="mt-4 text-3xl md:text-5xl font-extrabold text-white tracking-tight">
            {BUNDLE_NAME}. One Payment. Forever.
          </h2>

          <div className="mt-10 relative rounded-3xl border border-emerald-400/25 bg-gradient-to-b from-white/[0.05] to-transparent p-8 md:p-12 shadow-[0_0_80px_-20px_rgba(16,185,129,0.4)] overflow-hidden">
            <span className="absolute inset-0 med-shimmer pointer-events-none opacity-40" />

            {/* Original price with animated red slash */}
            <div className="relative">
              <div className="text-slate-300 text-sm uppercase tracking-widest">Original Price</div>
              <div className="mt-2 relative inline-block">
                <span className="text-4xl md:text-5xl font-extrabold text-white/85">
                  {fmt(BUNDLE_ORIGINAL_PRICE)}
                </span>
                {revealed && <span className="med-slash absolute inset-0" />}
              </div>
            </div>

            {/* BOOM badge */}
            <div className="my-6 flex items-center justify-center">
              {boom ? (
                <div className="med-boom inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-rose-500 via-orange-500 to-amber-400 text-slate-900 text-sm md:text-base font-extrabold uppercase tracking-wider shadow-[0_10px_40px_-10px_rgba(251,146,60,0.7)] border-2 border-white/20">
                  <Sparkles size={16} className="text-slate-900" />
                  BOOM! Special Offer Activated!
                  <Sparkles size={16} className="text-slate-900" />
                </div>
              ) : (
                <div className="h-11" />
              )}
            </div>

            {/* Final Price */}
            <div className={boom ? 'med-price-in' : 'opacity-0'}>
              <div className="text-amber-200 text-sm uppercase tracking-widest font-semibold">Today's Price</div>
              <div className="mt-3 flex items-end justify-center gap-2">
                <span
                  data-testid="offer-todays-price"
                  className="text-6xl md:text-8xl font-extrabold bg-gradient-to-r from-emerald-300 to-amber-200 bg-clip-text text-transparent leading-none"
                >
                  Just {fmt(BUNDLE_PRICE)}!
                </span>
              </div>
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-200 text-xs font-bold">
                YOU SAVE {fmt(BUNDLE_SAVINGS)} TODAY ({BUNDLE_DISCOUNT_PCT}% OFF)!
              </div>

              <div className="mt-8">
                <PrimaryCTA />
              </div>
              <BuyLine />

              <div className="mt-5 flex flex-col items-center gap-1 text-slate-300 text-sm">
                <div className="inline-flex items-center gap-2">
                  <Zap size={14} className="text-amber-300 fill-amber-300" />
                  Instant lifetime download links delivered to your email
                </div>
                <div className="inline-flex items-center gap-2 text-slate-400 text-xs">
                  <Lock size={12} /> Secure Payment &middot; Cards, Apple Pay &amp; Wallets accepted
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section className="relative py-24 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-400/30 bg-amber-400/5 text-amber-200 text-xs font-semibold uppercase tracking-widest">
              Real Transformations
            </div>
            <h2 className="mt-4 text-3xl md:text-5xl font-extrabold text-white tracking-tight">
              Loved by <span className="bg-gradient-to-r from-emerald-300 to-amber-200 bg-clip-text text-transparent">10,000+ Souls</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {meditationTestimonials.map((t) => (
              <div key={t.name} className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-transparent p-6">
                <div className="flex gap-0.5 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className="fill-amber-300 text-amber-300" />
                  ))}
                </div>
                <p className="text-slate-200 text-sm italic leading-relaxed">&ldquo;{t.text}&rdquo;</p>
                <div className="mt-5 flex items-center gap-3">
                  <img
                    src={t.photo}
                    alt={t.name}
                    loading="lazy"
                    decoding="async"
                    width="44"
                    height="44"
                    className="w-11 h-11 rounded-full object-cover border-2 border-emerald-400/40"
                  />
                  <div>
                    <div className="text-white font-bold text-sm">{t.name}</div>
                    <div className="text-slate-400 text-xs">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <PrimaryCTA />
            <BuyLine />
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="relative py-24 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight">Common Questions</h2>
            <p className="mt-3 text-slate-400">Everything you need to know before claiming your library</p>
          </div>
          <div className="space-y-3">
            {meditationFaqs.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} open={openFaq === i} onClick={() => setOpenFaq(openFaq === i ? -1 : i)} />
            ))}
          </div>

          <div className="mt-12 text-center">
            <PrimaryCTA />
            <BuyLine />
          </div>
        </div>
      </section>

      {/* ============ TRUST STRIP + FOOTER ============ */}
      <footer className="relative border-t border-white/5 py-14">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <Award className="text-emerald-300 mx-auto" size={28} />
              <div className="mt-3 text-white font-bold">Instant Cloud Delivery</div>
              <div className="mt-1 text-slate-400 text-sm">Direct lifetime download links delivered to your email immediately upon payment.</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <InfinityIcon className="text-emerald-300 mx-auto" size={28} />
              <div className="mt-3 text-white font-bold">Lifetime Access</div>
              <div className="mt-1 text-slate-400 text-sm">One payment. Yours forever. No monthly fees, no renewals, no expiry.</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <Lock className="text-emerald-300 mx-auto" size={28} />
              <div className="mt-3 text-white font-bold">Universal Compatibility</div>
              <div className="mt-1 text-slate-400 text-sm">MP3 audio &amp; PDF format &mdash; works on all phones, tablets, and laptops.</div>
            </div>
          </div>

          <div className="mt-10 flex flex-col md:flex-row items-center justify-between gap-4 text-slate-500 text-xs">
            <div>&copy; 2025 Mind to Master. All rights reserved.</div>
            <div className="flex items-center gap-5">
              <a href="#" className="hover:text-emerald-300 transition-colors">Terms</a>
              <a href="#" className="hover:text-emerald-300 transition-colors">Privacy</a>
              <a href="#" className="hover:text-emerald-300 transition-colors">Support</a>
            </div>
          </div>
          <div className="mt-6 text-center text-slate-600 text-[11px] max-w-3xl mx-auto leading-relaxed">
            Disclaimer: This mind-mastery library is provided for personal wellness and mindfulness purposes. It is not a substitute for professional medical or psychological treatment. Individual results may vary.
          </div>
        </div>
      </footer>

      <MeditationPurchaseNotifier variant="landing" />

      {/* Mobile sticky bottom CTA — always visible on small screens for conversion */}
      <div
        data-testid="mobile-sticky-cta"
        className="md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-white/10 bg-[#050813]/95 backdrop-blur-md px-3 py-2.5"
      >
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-slate-400 text-[10px] uppercase tracking-widest">Launch price</div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-slate-500 text-xs line-through decoration-rose-500">{fmt(BUNDLE_ORIGINAL_PRICE)}</span>
              <span className="text-white text-lg font-extrabold">{fmt(BUNDLE_PRICE)}</span>
              <span className="text-emerald-300 text-[10px] font-bold">80% OFF</span>
            </div>
          </div>
          <button
            type="button"
            data-testid="mobile-sticky-claim"
            onClick={() => triggerClaim()}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full font-bold text-slate-900 text-sm bg-gradient-to-r from-emerald-300 to-emerald-500 shadow-[0_6px_20px_-4px_rgba(16,185,129,0.6)]"
          >
            Claim <ArrowRight size={14} />
          </button>
        </div>
      </div>

      {/* Celebration overlay — fires when any Claim CTA is clicked, then navigates to checkout */}
      <BuyCelebration
        open={celebrate}
        price={BUNDLE_PRICE}
        onComplete={() => {
          setCelebrate(false);
          navigate('/checkout');
        }}
      />
    </div>
  );
}
